import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import logo from "../assets/vgh-logo-ink.png";
import OrbitWordmark from "../components/OrbitWordmark";

/**
 * Sign in.
 *
 * The error text for a wrong password and for an address that does not exist
 * is deliberately identical, and it is identical on the server too. Telling
 * them apart is how somebody confirms which of the six addresses are real
 * before they start guessing at passwords. Do not "improve" this.
 *
 * The one exception is the lockout, which the server answers with a 429 and a
 * time -- a person who typed their own password wrong eight times needs to
 * know why it stopped working.
 */
export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await signIn(email, password);
      navigate(user.must_change_password ? "/password" : "/", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail
          : "Could not reach the server. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={onSubmit}>
        <img className="auth-logo" src={logo} alt="Vineyard Group Holdings" />
        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <button className="primary" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <p className="form-hint">Forgot it? Ask whoever set up your account.</p>

        <a
          className="powered"
          href="https://orbitcustom.com"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Powered by Orbit — orbitcustom.com"
        >
          <span>Powered by</span>
          <OrbitWordmark tone="light" />
        </a>
      </form>
    </div>
  );
}
